import cn from 'classnames';

import { Avatar } from 'components/Avatar';
import {
  highlightHtmlStringToString,
  HighlightString,
} from 'components/HighlightString';
import { IconOffice } from 'components/UI/Icons/Office';
import {
  getAvatarImageProps,
  type ProfileImageFields,
} from 'helpers/profileImage';

interface CandidateCard extends ProfileImageFields {
  buttons?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  company?: string | null;
  is_client: boolean;
  name?: string | null;
  role?: string | null;
  /** Shown last in the text column (after company and optional body). */
  tags?: React.ReactNode;
}

export function CandidateCard({
  buttons,
  children,
  className,
  company,
  image,
  is_client,
  name,
  profile_image_url,
  profile_img,
  role,
  tags,
}: CandidateCard) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 gap-x-3 rounded-[12px] bg-white p-3',
        className,
      )}
    >
      <Avatar
        {...getAvatarImageProps({ image, profile_image_url, profile_img })}
        isClient={is_client}
        placeholderName={name ? highlightHtmlStringToString(name) : undefined}
        shape="rounded"
        size={40}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-y-1">
        <div className="relative min-w-0">
          {name ? (
            <span
              className={cn(
                'block min-w-0 text-14/100% font-medium leading-tight text-gray-800',
                buttons != null && 'pe-[4.5rem]',
              )}
            >
              <HighlightString>{name}</HighlightString>
            </span>
          ) : null}
          {buttons != null ? (
            <span className="absolute right-0 top-0 z-10 flex items-center gap-0.5">
              {buttons}
            </span>
          ) : null}
        </div>
        {role ? (
          <span className="flex min-w-0 text-12/140% font-medium text-gray-600">
            <HighlightString>{role}</HighlightString>
          </span>
        ) : null}
        {company && (
          <span className="flex min-w-0 gap-x-1 text-12/140% font-medium text-gray-600">
            <span className="flex h-4 shrink-0 items-center [&>svg]:h-3 [&>svg]:w-3">
              <IconOffice />
            </span>
            <HighlightString>{company}</HighlightString>
          </span>
        )}
        {children}
        {tags ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-0.5 gap-y-0.5">
            {tags}
          </div>
        ) : null}
      </div>
    </div>
  );
}
